/**
 * People & Access data — the console side of the `access` module (#49). Reads the
 * management views (list_users/roles/teams) and drives the governed write verbs
 * (assign_role, grant_privilege, team membership, user status). Admin-gated server-side.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'
import { reconcileRelationships } from '../data/queries'

export interface AccessUser {
  /**
   * The stable record identity (`core.app_user.id`, the PK since core 0018).
   * Use it for React keys and test ids. It is NOT what the access verbs take:
   * assign_role, revoke_role, set_user_status, add_team_member, grant_record
   * and the persona verbs all key on the LOGIN, `auth_user_id` (#1053 B ruling,
   * issue comment 46374).
   */
  id: string
  /** The optional login binding. NULL until a provisioned user first signs in. */
  auth_user_id: string | null
  email: string | null
  display_name: string | null
  status: string
  idp: string | null
  roles: string[]
  is_platform_admin: boolean
}
export interface AccessRole { key: string; module: string; label: string; users: number; privileges: number }
export interface Privilege { entity_type: string; privilege: string; scope: string }
export interface Team { id: string; name: string; members: number; parent_id: string | null }
export interface TeamMember { user_id: string; display_name: string | null; role: string }
export interface TeamRole { role_key: string; label: string }

/** The console AREAS the current user may enter (backstage/customer/partner) — role-area
 *  gating (#37). Data stays RLS-enforced regardless; this hides sections they can't use. */
export function useMyAreas() {
  const { client } = useSolum()
  return useQuery<string[]>({
    queryKey: ['access', 'areas'],
    queryFn: async () => {
      /**
       * `access.my_areas()` returns `setof text`, and PostgREST serialises a
       * scalar-returning set function as a bare array of STRINGS — `["backstage"]`
       * — not as rows of objects. The fixture returns `[{my_areas:'backstage'}]`,
       * the object shape, so this used to `.map(r => r.my_areas)` and get
       * `[undefined]` against a real backend.
       *
       * The consequence was not subtle: AppShell reads `customerOnly =
       * !areas.includes('backstage')`, so EVERY backstage user — including a
       * platform admin — was bounced to /my-org and shown the customer area.
       * Found on the cutover rehearsal by opening /access against a live box and
       * getting the "My Organization" page.
       *
       * Both shapes are accepted rather than one being declared correct: the
       * fixture is not wrong to model rows, and PostgREST is not going to change.
       */
      const rows = await client.raw().rpcTable<{ my_areas: string } | string>('my_areas', {}, 'access')
      return (rows ?? []).map((r) => (typeof r === 'string' ? r : r?.my_areas)).filter(Boolean) as string[]
    },
  })
}

export function useAccessUsers() {
  const { client } = useSolum()
  return useQuery<AccessUser[]>({ queryKey: ['access', 'users'], queryFn: () => client.raw().rpcTable<AccessUser>('list_users', {}, 'access') })
}
/**
 * The GOVERNED directory search — `access.search_users` (access 2026090203).
 *
 * Slice B shipped this door and its MCP tool; the People & Access face the
 * 46374 ruling asked for ("the same result through People & Access, Command-K
 * and MCP") was never built, so until now the only human way to find a person
 * was to read the whole list. This is that face, and it calls the same door the
 * agent calls rather than filtering the admin list client-side — a client-side
 * filter would answer a different question (substring on an address the door
 * refuses to match on) and would write no audit event.
 *
 * SUBMIT-DRIVEN, and that is a security decision rather than a UX preference.
 * Every address-SHAPED query writes one audit event inside the door — matched,
 * miss or ambiguous — because a burst of misses IS the harvesting signal the
 * control exists to catch. Firing per keystroke would stamp a miss for every
 * prefix of every address anyone ever types and drown that signal in noise
 * produced by the console itself.
 *
 * The projection is narrower than `list_users` ON PURPOSE: no email column
 * exists on this door. The rows are therefore joined back to the admin list for
 * display — the door decides WHO matches, the admin list says what an admin may
 * see about them.
 */
export interface DirectoryHit { id: string; display_name: string | null; status: string; is_platform_admin: boolean }

/** The door's own ceiling — `least(coalesce(p_limit, 10), 25)`. Asking for more returns 25. */
export const DIRECTORY_SEARCH_LIMIT = 25

export function useSearchUsers(query: string, limit: number = DIRECTORY_SEARCH_LIMIT) {
  const { client } = useSolum()
  const q = query.trim()
  return useQuery<DirectoryHit[]>({
    queryKey: ['access', 'search-users', q, limit],
    enabled: q.length > 0,
    queryFn: () => client.raw().rpcTable<DirectoryHit>('search_users', { p_query: q, p_limit: limit }, 'access'),
  })
}

/**
 * Join a directory answer back to the admin list.
 *
 * The door decides WHO matches and IN WHAT ORDER (by_key first, then rank); the
 * admin list decides what an admin may see about them. Two rules, both load-bearing:
 *
 *  • the door's order is preserved — re-sorting here would silently discard the
 *    ranking the door computed and put an exact address confirmation below a
 *    name hit, which is the one thing its `order by by_key desc` exists to stop;
 *  • a hit the admin list does not carry is RETURNED, not dropped. It should be
 *    impossible — both doors read `core.app_user` for the same platform admin —
 *    so if it ever happens it is a finding, and a silently shorter list is
 *    exactly how such a finding would hide.
 */
export function joinDirectoryHits(list: AccessUser[], hits: DirectoryHit[] | undefined):
  { shown: AccessUser[]; unresolved: DirectoryHit[] } {
  const byId = new Map(list.map((u) => [u.id, u]))
  const shown: AccessUser[] = []
  const unresolved: DirectoryHit[] = []
  for (const h of hits ?? []) {
    const row = byId.get(h.id)
    if (row) shown.push(row); else unresolved.push(h)
  }
  return { shown, unresolved }
}

export function useAccessRoles() {
  const { client } = useSolum()
  return useQuery<AccessRole[]>({ queryKey: ['access', 'roles'], queryFn: () => client.raw().rpcTable<AccessRole>('list_roles', {}, 'access') })
}
export function useRolePrivileges(roleKey: string | null) {
  const { client } = useSolum()
  return useQuery<Privilege[]>({ queryKey: ['access', 'privs', roleKey], enabled: !!roleKey,
    queryFn: () => client.raw().rpcTable<Privilege>('role_privileges', { p_role_key: roleKey }, 'access') })
}
export function useTeams() {
  const { client } = useSolum()
  return useQuery<Team[]>({ queryKey: ['access', 'teams'], queryFn: () => client.raw().rpcTable<Team>('list_teams', {}, 'access') })
}
export function useTeamMembers(teamId: string | null) {
  const { client } = useSolum()
  return useQuery<TeamMember[]>({ queryKey: ['access', 'members', teamId], enabled: !!teamId,
    queryFn: () => client.raw().rpcTable<TeamMember>('team_members', { p_team_id: teamId }, 'access') })
}
export function useTeamRoles(teamId: string | null) {
  const { client } = useSolum()
  return useQuery<TeamRole[]>({ queryKey: ['access', 'teamRoles', teamId], enabled: !!teamId,
    queryFn: () => client.raw().rpcTable<TeamRole>('team_roles', { p_team_id: teamId }, 'access') })
}

function useAccessMutation<V, R = unknown>(fn: (client: ReturnType<typeof useSolum>['client'], v: V) => Promise<R>, invalidate: string[],
  /** Reads OUTSIDE this page that the verb also moved — awaited (#2000). */
  after?: (qc: ReturnType<typeof useQueryClient>, v: V) => Promise<void>) {
  const { client } = useSolum(); const qc = useQueryClient()
  // Every `useAccessMutation` call site reports its own failure through
  // `mutate(vars, { onError })` — a per-call callback the mutation-cache net
  // cannot see. Declared once, here, so the net does not double-report (#877).
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (v: V) => fn(client, v),
    /* The access reads are RETURNED too (#2000 audit). Voiding them meant the
       org chart could close over its own stale list while only the relationship
       half was awaited — the surface that shows the change is the one that has
       to have caught up. */
    onSuccess: (_result, v) => Promise.all([
      ...invalidate.map((k) => qc.invalidateQueries({ queryKey: ['access', k] })),
      after?.(qc, v) ?? Promise.resolve(),
    ]).then(() => undefined),
  })
}

export const useAssignRole = () => useAccessMutation<{ userId: string; roleKey: string }>(
  (c, v) => c.raw().rpcScalar('assign_role', { p_user_id: v.userId, p_role_key: v.roleKey }, 'access'), ['users', 'roles'])
export const useRevokeRole = () => useAccessMutation<{ userId: string; roleKey: string }>(
  (c, v) => c.raw().rpcScalar('revoke_role', { p_user_id: v.userId, p_role_key: v.roleKey }, 'access'), ['users', 'roles'])
export const useSetUserStatus = () => useAccessMutation<{ userId: string; status: string }>(
  (c, v) => c.raw().rpcScalar('set_user_status', { p_auth_user_id: v.userId, p_status: v.status }, 'access'), ['users'])
export const useAppointPlatformAdmin = () => useAccessMutation<{ userId: string }, boolean>(
  (c, v) => c.raw().rpcScalar<boolean>('appoint_platform_admin', { p_auth_user_id: v.userId }, 'access'), ['users', 'review'])
export const useRemovePlatformAdmin = () => useAccessMutation<{ userId: string }, boolean>(
  (c, v) => c.raw().rpcScalar<boolean>('remove_platform_admin', { p_auth_user_id: v.userId }, 'access'), ['users', 'review'])
export const useCreateRole = () => useAccessMutation<{ key: string; module: string; label: string }>(
  (c, v) => c.raw().rpcScalar('create_role', { p_key: v.key, p_module: v.module, p_label: v.label }, 'access'), ['roles'])
export const useGrantPrivilege = () => useAccessMutation<{ roleKey: string; entityType: string; privilege: string; scope: string }>(
  (c, v) => c.raw().rpcScalar('grant_privilege', { p_role_key: v.roleKey, p_entity_type: v.entityType, p_privilege: v.privilege, p_scope: v.scope }, 'access'), ['roles', 'privs'])
export const useRevokePrivilege = () => useAccessMutation<{ roleKey: string; entityType: string; privilege: string; scope: string }>(
  (c, v) => c.raw().rpcScalar('revoke_privilege', { p_role_key: v.roleKey, p_entity_type: v.entityType, p_privilege: v.privilege, p_scope: v.scope }, 'access'), ['roles', 'privs'])
export const useCreateTeam = () => useAccessMutation<{ name: string }>(
  (c, v) => c.raw().rpcScalar('create_team', { p_name: v.name }, 'access'), ['teams'])
export const useAddTeamMember = () => useAccessMutation<{ teamId: string; userId: string; role: string }>(
  (c, v) => c.raw().rpcScalar('add_team_member', { p_team_id: v.teamId, p_user_id: v.userId, p_role: v.role }, 'access'), ['teams', 'members'])
export const useRemoveTeamMember = () => useAccessMutation<{ teamId: string; userId: string }>(
  (c, v) => c.raw().rpcScalar('remove_team_member', { p_team_id: v.teamId, p_user_id: v.userId }, 'access'), ['teams', 'members'])
export const useAssignTeamRole = () => useAccessMutation<{ teamId: string; roleKey: string }>(
  (c, v) => c.raw().rpcScalar('assign_team_role', { p_team_id: v.teamId, p_role_key: v.roleKey }, 'access'), ['teamRoles'])
export const useRevokeTeamRole = () => useAccessMutation<{ teamId: string; roleKey: string }>(
  (c, v) => c.raw().rpcScalar('revoke_team_role', { p_team_id: v.teamId, p_role_key: v.roleKey }, 'access'), ['teamRoles'])

// ── Reporting line (manager hierarchy) — persons + reports_to edges (client-joined) ──
export interface Person { id: string; display_name: string | null }
export interface ReportEdge { from_id: string; to_id: string }

export function usePeople() {
  const { client } = useSolum()
  return useQuery<Person[]>({ queryKey: ['access', 'people'],
    queryFn: () => client.raw().select<Person>('core', 'person', { columns: 'id,display_name', limit: 1000 }) })
}
export function useReportsTo() {
  const { client } = useSolum()
  return useQuery<ReportEdge[]>({ queryKey: ['access', 'reports'],
    queryFn: () => client.raw().select<ReportEdge>('core', 'relationship', { columns: 'from_id,to_id', where: { rel_type: 'reports_to' }, limit: 2000 }) })
}
/** Every end of a reporting line is a `core_person` record with its own band. */
const PERSON = 'core_person'

/**
 * `access.set_manager` DELETES every `reports_to` edge out of the person and
 * inserts at most one (access 0002), so it moves the relationship rows and
 * counts of up to THREE records: the person, the manager gaining a report, and
 * the manager who has just lost one.
 *
 * All three are reconciled (#2000). Refreshing only this page's own reads was
 * tried and is not enough — the org chart is not the only surface that can be
 * open, and a person record in another pane would keep a `reports_to` band the
 * server has already changed. "The surface I wrote from does not render a band"
 * is not a boundary; it is one surface out of several.
 *
 * The PREVIOUS manager is the end a source-local refresh cannot reach at all,
 * because nothing in the NEW state names them. The caller passes them from the
 * edges it has already read. A call site that genuinely cannot know omits the
 * argument and leaves exactly one stale band — which is why it is explicit
 * rather than derived here: this hook has no way to look it up, and guessing
 * would be worse than the omission being visible.
 */
export const useSetManager = () => useAccessMutation<{ personId: string; managerId: string | null; previousManagerId?: string | null }>(
  (c, v) => c.raw().rpcScalar('set_manager', { p_person_id: v.personId, p_manager_id: v.managerId }, 'access'), ['people', 'reports'],
  (qc, v) => Promise.all([
    reconcileRelationships(qc, PERSON, v.personId),
    v.managerId ? reconcileRelationships(qc, PERSON, v.managerId) : Promise.resolve(),
    v.previousManagerId && v.previousManagerId !== v.managerId
      ? reconcileRelationships(qc, PERSON, v.previousManagerId) : Promise.resolve(),
  ]).then(() => undefined))

// ── Project-granular record grants (share a specific record with a user/team) ──
export interface RecordGrant { subject_kind: string; subject_id: string; subject_name: string | null; privilege: string }
export function useRecordGrants(entityType: string, recordId: string | null) {
  const { client } = useSolum()
  return useQuery<RecordGrant[]>({ queryKey: ['access', 'record-grants', entityType, recordId], enabled: !!recordId,
    queryFn: () => client.raw().rpcTable<RecordGrant>('record_grants', { p_entity_type: entityType, p_record_id: recordId }, 'access') })
}
export const useGrantRecord = () => useAccessMutation<{ subjectKind: string; subjectId: string; entityType: string; recordId: string; privilege: string }>(
  (c, v) => c.raw().rpcScalar('grant_record', { p_subject_kind: v.subjectKind, p_subject_id: v.subjectId, p_entity_type: v.entityType, p_record_id: v.recordId, p_privilege: v.privilege }, 'access'), ['record-grants'])
export const useRevokeRecord = () => useAccessMutation<{ subjectKind: string; subjectId: string; entityType: string; recordId: string; privilege: string }>(
  (c, v) => c.raw().rpcScalar('revoke_record', { p_subject_kind: v.subjectKind, p_subject_id: v.subjectId, p_entity_type: v.entityType, p_record_id: v.recordId, p_privilege: v.privilege }, 'access'), ['record-grants'])

// ── Customer self-service: my orgs + org membership (org-scoped, delegated) ──
export interface MyOrg { org_id: string; name: string; role: string }
export interface OrgMember { auth_user_id: string; display_name: string | null; email: string | null; role: string }
export function useMyOrgs() {
  const { client } = useSolum()
  return useQuery<MyOrg[]>({ queryKey: ['access', 'my-orgs'], queryFn: () => client.raw().rpcTable<MyOrg>('my_orgs', {}, 'access') })
}
export function useOrgMembers(orgId: string | null) {
  const { client } = useSolum()
  return useQuery<OrgMember[]>({ queryKey: ['access', 'org-members', orgId], enabled: !!orgId,
    queryFn: () => client.raw().rpcTable<OrgMember>('org_members', { p_org_id: orgId }, 'access') })
}
export const useSetOrgMembership = () => useAccessMutation<{ userId: string; orgId: string; role: string }>(
  (c, v) => c.raw().rpcScalar('set_org_membership', { p_auth_user_id: v.userId, p_org_id: v.orgId, p_role: v.role }, 'access'), ['org-members', 'my-orgs'])
export const useRemoveOrgMembership = () => useAccessMutation<{ userId: string; orgId: string }>(
  (c, v) => c.raw().rpcScalar('remove_org_membership', { p_auth_user_id: v.userId, p_org_id: v.orgId }, 'access'), ['org-members'])

/* ══════════════════════════════════════════════════════════════════════════
 * The security ANSWER surface (core 0102/0104, access 0014/0015).
 *
 * These are the reads that make the model explainable instead of merely
 * configurable — the half Dataverse never exposes. Every one is admin-gated
 * server-side, so a non-admin loading this page gets an error, not a blank.
 * ══════════════════════════════════════════════════════════════════════════ */

export interface AccessFactor { verdict: string; factor: string; effect: string; detail: string }
export interface Viewer { principal: string; email: string | null; reasons: string[] }
/**
 * One row of `core.access_review()`.
 *
 * `privileges` was typed `unknown` for as long as nothing drew it — the type
 * was an accurate record of a door column with no face (rule 41). It is the
 * door's own `jsonb_agg(jsonb_build_object('entity', …, 'privilege', …,
 * 'scope', …))`, so the names here are the door's names: `entity`, not
 * `entity_type`.
 */
export interface HeldPrivilege { entity: string; privilege: string; scope: string }
export interface ReviewRow {
  principal: string; email: string | null; is_platform_admin: boolean
  roles: string[]; privileges: HeldPrivilege[]; record_grants: number; delegations: number; sources: string[]
}
export interface DriftRow {
  attested_at: string | null; reviewer: string | null; changed: boolean
  added: string[]; removed: string[]; altered: string[]
}
export interface TenancyRow { entity_type: string; tenant_scope: string; via: string | null; carries_pii: boolean; note: string | null }
export interface TenancyHealth { mode: string; resolver: string; unclassified: number; healthy: boolean; note: string }
export interface ReportsConfigRow { entity_key: string; label: string; max_depth: number | null; depth_label: string; enabled_at: string | null }

/** "Why can this principal see this record?" — the verdict is MEASURED under real RLS. */
export function useExplainAccess(user: string | null, entity: string | null, record: string | null, privilege = 'read') {
  const { client } = useSolum()
  return useQuery<AccessFactor[]>({
    queryKey: ['access', 'explain', user, entity, record, privilege],
    enabled: !!user && !!entity && !!record,
    queryFn: () => client.raw().rpcTable<AccessFactor>('explain_access',
      { p_user: user, p_entity: entity, p_record: record, p_privilege: privilege }, 'core'),
  })
}

/** The record-centric half — everyone who can see one row, and why. */
export function useWhoCanSee(entity: string | null, record: string | null, privilege = 'read') {
  const { client } = useSolum()
  return useQuery<Viewer[]>({
    queryKey: ['access', 'whoCanSee', entity, record, privilege],
    enabled: !!entity && !!record,
    queryFn: () => client.raw().rpcTable<Viewer>('who_can_see',
      { p_entity: entity, p_record: record, p_privilege: privilege }, 'core'),
  })
}

export function useAccessReview() {
  const { client } = useSolum()
  return useQuery<ReviewRow[]>({ queryKey: ['access', 'review'],
    queryFn: () => client.raw().rpcTable<ReviewRow>('access_review', {}, 'core') })
}

export function useAccessDrift() {
  const { client } = useSolum()
  return useQuery<DriftRow[]>({ queryKey: ['access', 'drift'],
    queryFn: () => client.raw().rpcTable<DriftRow>('access_drift_since_attestation', {}, 'core') })
}

export function useTenancyReport() {
  const { client } = useSolum()
  return useQuery<TenancyRow[]>({ queryKey: ['access', 'tenancy'],
    queryFn: () => client.raw().rpcTable<TenancyRow>('tenancy_report', {}, 'core') })
}

export function useTenancyHealth() {
  const { client } = useSolum()
  return useQuery<TenancyHealth[]>({ queryKey: ['access', 'tenancyHealth'],
    queryFn: () => client.raw().rpcTable<TenancyHealth>('tenancy_health', {}, 'core') })
}

export function useReportsConfig() {
  const { client } = useSolum()
  return useQuery<ReportsConfigRow[]>({ queryKey: ['access', 'reportsConfig'],
    queryFn: () => client.raw().rpcTable<ReportsConfigRow>('reports_config', {}, 'access') })
}

export const useAttestReview = () => useAccessMutation<{ finding: string; scope?: string }>(
  (c, v) => c.raw().rpcScalar('attest_access_review', { p_finding: v.finding, p_scope: v.scope ?? 'all' }, 'core'),
  ['review', 'drift'])

export const useRevokeAllAccess = () => useAccessMutation<{ principal: string; reason: string }>(
  (c, v) => c.raw().rpcScalar('revoke_access', { p_principal: v.principal, p_reason: v.reason }, 'core'),
  ['review', 'drift', 'users'])

export const useEnableReportsVisibility = () => useAccessMutation<{ entityKey: string; enabled: boolean; maxDepth: number | null }>(
  (c, v) => c.raw().rpcScalar('enable_reports_visibility',
    { p_entity_key: v.entityKey, p_enabled: v.enabled, p_max_depth: v.maxDepth }, 'access'),
  ['reportsConfig'])

export const useEnableTenancy = () => useAccessMutation<{ enabled: boolean }>(
  (c, v) => c.raw().rpcScalar('enable_tenancy', { p_enabled: v.enabled }, 'core'),
  ['tenancy', 'tenancyHealth'])
